// Main-thread helper for streaming ZIP downloads via Service Worker

let swReady = null;

async function ensureSW() {
  if (!('serviceWorker' in navigator)) return false;
  if (swReady !== null) return swReady;

  swReady = new Promise((resolve) => {
    const timeout = setTimeout(() => resolve(false), 3000);
    navigator.serviceWorker.register('/sw.js')
      .then((reg) => {
        const sw = reg.active || reg.installing || reg.waiting;
        if (!sw) { clearTimeout(timeout); resolve(false); return; }
        if (sw.state === 'activated') { clearTimeout(timeout); resolve(true); return; }
        sw.addEventListener('statechange', () => {
          if (sw.state === 'activated') { clearTimeout(timeout); resolve(true); }
        });
      })
      .catch(() => { clearTimeout(timeout); resolve(false); });
  });
  return swReady;
}

/**
 * Create a streaming download channel via the Service Worker.
 * Returns { writer, cleanup } or null if SW is unavailable.
 *
 * writer.write(Uint8Array) — send a chunk (zero-copy via Transferable)
 * writer.close()           — signal end of stream
 * writer.abort(reason)     — signal error
 * cleanup()                — remove the hidden iframe
 */
export async function createStreamDownload(filename) {
  const ok = await ensureSW();
  if (!ok || !navigator.serviceWorker.controller) return null;

  const id = crypto.randomUUID();
  const channel = new MessageChannel();

  // Send port2 to the SW
  navigator.serviceWorker.controller.postMessage(
    { type: 'init', id, port: channel.port2 },
    [channel.port2],
  );

  // Create hidden iframe to trigger the fetch that the SW intercepts
  const iframe = document.createElement('iframe');
  iframe.hidden = true;
  iframe.src = `/sw-download/${id}?filename=${encodeURIComponent(filename)}`;
  document.body.appendChild(iframe);

  const port = channel.port1;

  const writer = {
    write(chunk) {
      // Transfer the underlying buffer for zero-copy
      if (chunk.buffer && chunk.byteOffset === 0 && chunk.byteLength === chunk.buffer.byteLength) {
        port.postMessage(chunk, [chunk.buffer]);
      } else {
        // Slice to own buffer before transferring
        const copy = chunk.slice();
        port.postMessage(copy, [copy.buffer]);
      }
    },
    close() {
      port.postMessage('end');
    },
    abort(reason) {
      port.postMessage({ error: reason || 'aborted' });
    },
  };

  function cleanup() {
    try { iframe.remove(); } catch (_) {}
  }

  return { writer, cleanup };
}
